import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import {
  ActividadGrupal,
  ActividadGrupalService,
  CampoDocumentoAsistenciaGrupal,
} from '../../services/actividad-grupal.service';
import { ActividadGrupalPanelService } from '../../services/actividad-grupal-panel.service';
import { NegocioService } from '../../services/negocio.service';
import { ToastService } from '../../services/toast.service';
import { VisitasService } from '../../services/visitas.service';
import {
  ASISTENCIA_ACTIVIDAD_GRUPAL_ACCEPT,
  validateAsistenciaActividadGrupalFile,
} from '../../utils/asistencia-actividad-grupal-upload-constraints';
import {
  actividadGrupalTieneDocumentoAsistencia,
  CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL,
  extraerAdjuntoPorCampo,
  type ActividadGrupalAsistenciaAdjunto,
} from '../../utils/actividad-grupal-asistencia-adjunto.util';
import { abrirODescargarAdjunto } from '../../utils/abrir-adjunto.util';

interface SlotDocumentoAsistencia {
  campo: CampoDocumentoAsistenciaGrupal;
  etiqueta: string;
  nombreCorto: string;
  icono: string;
  adjuntoExistente: ActividadGrupalAsistenciaAdjunto | null;
  archivoSeleccionado: File | null;
  nombreArchivoMostrar: string;
}

@Component({
  selector: 'app-formulario-asistencia-actividad-grupal',
  templateUrl: './formulario-asistencia-actividad-grupal.component.html',
  styleUrls: ['./formulario-asistencia-actividad-grupal.component.scss'],
  standalone: false,
})
export class FormularioAsistenciaActividadGrupalComponent implements OnChanges {
  @Input() visible = false;
  @Input() dentroDeMenu = false;
  @Input() actividad: ActividadGrupal | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() guardado = new EventEmitter<void>();

  readonly fileAccept = ASISTENCIA_ACTIVIDAD_GRUPAL_ACCEPT;
  readonly camposDocumento = CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL;

  slots: SlotDocumentoAsistencia[] = [];
  subiendo = false;
  eliminandoCampo: CampoDocumentoAsistenciaGrupal | null = null;
  teniaArchivoAlAbrir = false;

  private readonly actividadGrupalService = inject(ActividadGrupalService);
  private readonly actividadGrupalPanelService = inject(ActividadGrupalPanelService);
  private readonly negocioService = inject(NegocioService);
  private readonly visitasService = inject(VisitasService);
  private readonly toastService = inject(ToastService);
  private readonly alertController = inject(AlertController);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['actividad']) {
      this.inicializarSlots();
      if (changes['actividad'].firstChange || !changes['actividad'].previousValue) {
        this.teniaArchivoAlAbrir = actividadGrupalTieneDocumentoAsistencia(this.actividad);
      }
    }
  }

  get textoBotonPrincipal(): string {
    return this.teniaArchivoAlAbrir ? 'Guardar Cambios' : 'Subir Archivo';
  }

  get hayArchivosPendientes(): boolean {
    return this.slots.some((s) => s.archivoSeleccionado != null);
  }

  get accionesBloqueadas(): boolean {
    return this.subiendo || this.eliminandoCampo != null;
  }

  idInputArchivo(campo: CampoDocumentoAsistenciaGrupal): string {
    return `input-asistencia-actividad-${campo}`;
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetArchivosSeleccionados();
  }

  onArchivoChange(campo: CampoDocumentoAsistenciaGrupal, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const slot = this.obtenerSlot(campo);
    if (!slot) return;

    if (!file) {
      this.resetArchivoSlot(slot);
      return;
    }

    const check = validateAsistenciaActividadGrupalFile(file);
    if (!check.valid) {
      void this.toastService.mostrar(check.message, 'warning');
      input.value = '';
      this.resetArchivoSlot(slot);
      return;
    }

    slot.archivoSeleccionado = file;
    slot.nombreArchivoMostrar = file.name;
  }

  activarSelectorArchivo(input: HTMLInputElement): void {
    if (this.accionesBloqueadas) return;
    input.click();
  }

  verArchivoExistente(slot: SlotDocumentoAsistencia): void {
    if (!slot.adjuntoExistente || this.accionesBloqueadas) return;
    abrirODescargarAdjunto(
      slot.adjuntoExistente,
      this.negocioService,
      this.visitasService,
      this.toastService
    );
  }

  eliminarArchivoExistente(slot: SlotDocumentoAsistencia): void {
    if (!slot.adjuntoExistente || this.accionesBloqueadas) return;
    void this.confirmarYEliminar(slot);
  }

  estaEliminando(campo: CampoDocumentoAsistenciaGrupal): boolean {
    return this.eliminandoCampo === campo;
  }

  onSubirClick(): void {
    void this.subir();
  }

  private inicializarSlots(): void {
    const prev = new Map(this.slots.map((s) => [s.campo, s]));
    this.slots = CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL.map(({ campo, etiqueta, nombreCorto, icono }) => {
      const anterior = prev.get(campo);
      return {
        campo,
        etiqueta,
        nombreCorto,
        icono,
        adjuntoExistente: extraerAdjuntoPorCampo(this.actividad, campo),
        archivoSeleccionado: anterior?.archivoSeleccionado ?? null,
        nombreArchivoMostrar: anterior?.nombreArchivoMostrar ?? '',
      };
    });
  }

  private obtenerSlot(campo: CampoDocumentoAsistenciaGrupal): SlotDocumentoAsistencia | undefined {
    return this.slots.find((s) => s.campo === campo);
  }

  private async confirmarYEliminar(slot: SlotDocumentoAsistencia): Promise<void> {
    const id = this.actividad?.id_asesoria_especializada;
    if (id == null) {
      await this.toastService.mostrar('No se pudo identificar la actividad grupal.', 'error');
      return;
    }

    const alert = await this.alertController.create({
      header: 'Eliminar documento',
      message: `¿Está seguro de que desea eliminar el ${slot.nombreCorto}? Esta acción no se puede deshacer.`,
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Sí, eliminar', role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') return;

    this.eliminandoCampo = slot.campo;
    try {
      await firstValueFrom(
        this.actividadGrupalService.eliminarAsistenciaActividadGrupal(id, slot.campo)
      );
      await this.toastService.mostrar('Documento eliminado correctamente', 'success');
      this.marcarDocumentoEliminadoLocal(slot);
      this.guardado.emit();
    } catch (error: unknown) {
      const httpErr = error as HttpErrorResponse;
      if (httpErr?.status === 404) {
        const archivoInexistente = await this.archivoNoExisteEnServidor(
          slot.adjuntoExistente?.ruta_almacenamiento
        );
        if (archivoInexistente) {
          await this.toastService.mostrar(
            'El archivo ya no está disponible en el servidor. Puede quitar el registro con eliminar; si persiste el error, solicite corrección al administrador.',
            'warning'
          );
          return;
        }
      }

      const err = error as { error?: { detail?: string; message?: string } | string };
      const mensaje =
        (typeof err?.error === 'object' && (err?.error?.detail || err?.error?.message)) ||
        (typeof err?.error === 'string' ? err.error : null) ||
        'Error al eliminar el documento';
      await this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.eliminandoCampo = null;
    }
  }

  private async subir(): Promise<void> {
    const pendientes = this.slots.filter((s) => s.archivoSeleccionado != null);
    if (this.subiendo || pendientes.length === 0) {
      if (pendientes.length === 0) {
        await this.toastService.mostrar('Seleccione al menos un archivo.', 'warning');
      }
      return;
    }

    const id = this.actividad?.id_asesoria_especializada;
    if (id == null) {
      await this.toastService.mostrar('No se pudo identificar la actividad grupal.', 'error');
      return;
    }

    const slotsConReemplazo = pendientes.filter((s) => s.adjuntoExistente != null);
    for (const slot of slotsConReemplazo) {
      const confirmado = await this.confirmarReemplazo(slot);
      if (!confirmado) return;
    }

    this.subiendo = true;
    try {
      await this.subirArchivosPendientes(id, pendientes);
      await this.toastService.mostrar('Documentos subidos correctamente', 'success');
      this.guardado.emit();
      this.cerrar();
    } catch (error: unknown) {
      await this.mostrarErrorSubida(error);
    } finally {
      this.subiendo = false;
    }
  }

  /**
   * Un PUT por slot con archivo nuevo (`archivoSeleccionado`).
   * No incluye slots que solo tienen adjunto en servidor sin selección nueva.
   */
  private async subirArchivosPendientes(
    id: number,
    pendientes: SlotDocumentoAsistencia[]
  ): Promise<void> {
    for (const slot of pendientes) {
      const archivo = slot.archivoSeleccionado;
      if (!archivo) continue;
      await firstValueFrom(
        this.actividadGrupalService.subirAsistenciaActividadGrupal(id, slot.campo, archivo)
      );
    }
  }

  private async archivoNoExisteEnServidor(ruta: string | null | undefined): Promise<boolean> {
    if (!ruta?.trim()) return false;
    try {
      const blob = await firstValueFrom(this.negocioService.obtenerArchivoPorRuta(ruta));
      return !blob?.size;
    } catch (error: unknown) {
      const status = (error as HttpErrorResponse)?.status;
      return status === 404;
    }
  }

  private marcarDocumentoEliminadoLocal(slot: SlotDocumentoAsistencia): void {
    slot.adjuntoExistente = null;
    this.actividadGrupalPanelService.quitarDocumentoDeActividad(slot.campo);
    if (!this.slots.some((s) => s.adjuntoExistente != null)) {
      this.teniaArchivoAlAbrir = false;
    }
  }

  private async mostrarErrorSubida(error: unknown): Promise<void> {
    const err = error as { error?: { detail?: string; message?: string } | string };
    const detalleApi =
      typeof err?.error === 'object' ? err?.error?.detail || err?.error?.message : null;
    const mensaje =
      detalleApi ||
      (typeof err?.error === 'string' ? err.error : null) ||
      'Error al subir los documentos';

    if (mensaje === 'Archivo no encontrado') {
      await this.toastService.mostrar(
        'No se pudo subir el documento. Si la actividad tiene registros antiguos inconsistentes, solicite al administrador su corrección en el backend.',
        'error'
      );
      return;
    }

    await this.toastService.mostrar(mensaje, 'error');
  }

  private async confirmarReemplazo(slot: SlotDocumentoAsistencia): Promise<boolean> {
    const alert = await this.alertController.create({
      header: 'Reemplazar documento',
      message: `Ya existe un archivo para el ${slot.nombreCorto}. Si continúa, el documento anterior se eliminará de forma irreversible y será reemplazado por el nuevo archivo.`,
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Sí, reemplazar', role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private resetArchivoSlot(slot: SlotDocumentoAsistencia): void {
    slot.archivoSeleccionado = null;
    slot.nombreArchivoMostrar = '';
  }

  private resetArchivosSeleccionados(): void {
    for (const slot of this.slots) {
      this.resetArchivoSlot(slot);
    }
  }
}
